"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { getNavigation, type NavigationData } from "@/lib/api";
import { resolvePublicPageUrl } from "@/lib/api";

const NavigationContext = createContext<NavigationData | null>(null);

const fallback: NavigationData = {
  headerMenu: [],
  footerMenu: [],
  generalServices: [],
  services: [],
  products: [],
  industries: [],
  resources: [],
  company: [],
  footerLinks: {
    company: [],
    services: [],
    products: [],
    resources: [],
    legal: [],
  },
  social: [],
};

function normalizeMenuUrl(url: string) {
  if (!url) return url;
  return resolvePublicPageUrl(url);
}

function normalizeVisibilityFlag(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;
  }

  return undefined;
}

function normalizeMenuNodes<T extends { url: string; showOnDesktop?: unknown; showOnMobile?: unknown; children?: T[] }>(nodes: T[]): T[] {
  return nodes.map((node) => ({
    ...node,
    url: normalizeMenuUrl(node.url),
    showOnDesktop: normalizeVisibilityFlag(node.showOnDesktop),
    showOnMobile: normalizeVisibilityFlag(node.showOnMobile),
    children: normalizeMenuNodes((node.children || []) as T[]),
  }));
}

function normalizeLinkGroup<T extends { href: string }>(items: T[]): T[] {
  return items.map((item) => ({
    ...item,
    href: normalizeMenuUrl(item.href),
  }));
}

function normalizeNavigation(data: NavigationData): NavigationData {
  return {
    ...data,
    headerMenu: normalizeMenuNodes(data.headerMenu || []),
    footerMenu: normalizeMenuNodes(data.footerMenu || []),
    resources: normalizeLinkGroup(data.resources || []),
    company: normalizeLinkGroup(data.company || []),
    footerLinks: {
      company: normalizeLinkGroup(data.footerLinks?.company || []),
      services: normalizeLinkGroup(data.footerLinks?.services || []),
      products: normalizeLinkGroup(data.footerLinks?.products || []),
      resources: normalizeLinkGroup(data.footerLinks?.resources || []),
      legal: normalizeLinkGroup(data.footerLinks?.legal || []),
    },
  };
}

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [nav, setNav] = useState<NavigationData>(fallback);

  useEffect(() => {
    getNavigation()
      .then((data) => setNav(normalizeNavigation(data)))
      .catch(() => {});
  }, []);

  return (
    <NavigationContext.Provider value={nav}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation(): NavigationData {
  const ctx = useContext(NavigationContext);
  return ctx ?? fallback;
}
